// Angular
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

// RxJS
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Third-party
import { environment } from '@environment';
import { OAuthService } from 'angular-oauth2-oidc';

// Local
import { EmulationService } from '@core/auth/emulation.service';
import { loginReturnPath } from '@core/auth/login-return-path';
import { WINDOW } from '@core/window.factory';

export const BYPASS_TOKEN_INJECTION = new HttpContextToken(() => false);

/**
 * Opt a single request out of the app-wide 403 -> /forbidden redirect, when the caller surfaces the
 * failure itself. A 403 is resource-scoped ("you may not touch this"), not session-scoped, so
 * promoting one to a navigation event tears the user out of whatever they were doing — on a filing
 * draft that means losing the in-progress work (#172574, and a develop-wide lockout in July via
 * `GET /emulation/enabled`). Default `false` keeps the redirect for every request that has not been
 * reviewed. Only set this where the caller both handles and *shows* the error.
 */
export const BYPASS_403_REDIRECT = new HttpContextToken(() => false);

/** Header carrying the id of the user an admin is emulating. Mirrors the server-side constant. */
const IMPERSONATE_HEADER = 'X-Impersonate-User-Id';

/**
 * Base URL of the emulation endpoints. Requests to these must NOT carry the impersonation header, so
 * the picker/profile/enabled calls run as the real admin. Matched by prefix (not a loose substring).
 */
const EMULATION_API_PREFIX = `${environment.ApiServer.BaseURL}emulation`;

@Injectable({
  providedIn: 'root',
})
export class FSXInterceptor implements HttpInterceptor {

  // Tracks if this interceptor instance has already handled an auth error.
  private isHandlingAuthError = false;

  public constructor(
    private readonly oAuthService: OAuthService,
    private readonly router: Router,
    private readonly emulationService: EmulationService,
    @Inject(WINDOW) private readonly window: Window,
  ) {}

  // https://github.com/johnpapa/http-interceptors/tree/main/angular-app/src/app/core/interceptors
  // https://danielk.tech/home/angular-retry-an-http-request
  // https://www.tektutorialshub.com/angular/retry-retrywhen-in-angular-observable/#adding-a-delay

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const authToken = this.oAuthService.getAccessToken() ?? null;
    if (!request.context.get(BYPASS_TOKEN_INJECTION)) {
      request = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${authToken}`),
      });
    }

    // While emulating, attach the impersonation header so the server scopes data to the target user.
    // Skip the emulation endpoints themselves so the picker/profile calls run as the real admin.
    const targetUserId = this.emulationService.targetUserId();
    if (targetUserId && !request.url.startsWith(EMULATION_API_PREFIX)) {
      request = request.clone({
        headers: request.headers.set(IMPERSONATE_HEADER, targetUserId),
      });
    }

    return next.handle(request).pipe(
      catchError((httpError: HttpErrorResponse) => {
        const isUnauthorized = httpError.status === 401;
        const isForbidden = httpError.status === 403;

        // Handle 403 separately (e.g., redirect to 'forbidden' page), unless the caller has opted
        // out via BYPASS_403_REDIRECT because it surfaces the failure in place.
        if (isForbidden) {
          if (!request.context.get(BYPASS_403_REDIRECT)) {
            this.router.navigate(['/forbidden']);
          }
        } else if (isUnauthorized && !this.isHandlingAuthError) {
          // Any 401: redirect once (avoid loop). With valid token, logout (redirects to IdP). Otherwise start login flow.
          this.isHandlingAuthError = true;
          const hasValidToken = this.oAuthService.hasValidAccessToken();
          if (hasValidToken) {
            this.oAuthService.logOut();
          } else {
            // Back to this page after signing in again.
            this.oAuthService.initLoginFlow(loginReturnPath(this.window.location));
          }
        }

        return throwError(() => httpError);
      }),
    );
  }
}
